import { Component, inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ModalController } from '@ionic/angular';
import {
  ConstructionDayReportType,
  NodeItem,
  StateService,
  ToolService,
} from 'projects/shared/src/public-api';
import { ConstructionDayReport } from 'projects/shared/src/public-api';
import { Subscription } from 'rxjs';
import { ModalRole } from '../../models/modal-roles';
import { PdfType } from '../../models/pdf-type';

interface PreviewItem {
  id: string;
  imageUrl?: string;
  loading: boolean;
  hasError: boolean;
}
@Component({
  selector: 'lib-construction-day-report-detail',
  templateUrl: './construction-day-report-detail.component.html',
  styleUrls: ['./construction-day-report-detail.component.scss'],
  standalone: false,
})
export class ConstructionDayReportDetailComponent implements OnInit {
  private translate = inject(TranslateService);

  constructor(
    public state: StateService,
    public toolService: ToolService,
    public modalCtrl: ModalController
  ) {}

  isFreshReport: boolean = false; // if report was created in the last 30 min
  paramId: string;
  report: ConstructionDayReport;
  sameDayReports: ConstructionDayReport[] = [];
  tableValues: {
    value1: string;
    value2: boolean;
    value3: string;
    value4: string;
  }[] = [];
  private subs: Subscription[] = [];
  ConstructionDayReportType = ConstructionDayReportType;
  private constructionDayReportNodeItems: NodeItem[] = [];
  public attachedImageNodeItems: NodeItem[] = [];
  public attachedDocumentNodeItems: NodeItem[] = [];
  public previews: PreviewItem[] = [];

  ngOnInit() {
    this.subs.push(
      this.state.constructionDayReports$.subscribe((x) => {
        this.sameDayReports = [];
        this.report = x.find((x) => x.id == this.paramId);
        this.isFreshReport =
          Date.now() - new Date(this.report.sentDate).getTime() <=
          30 * 60 * 1000;
        this.sameDayReports.push(this.report);
        this.sameDayReports.push(
          ...x.filter(
            (y) =>
              y.constructionSite == this.report.constructionSite &&
              this.toolService.dateStringsAreSameDay(
                y.date,
                this.report.date
              ) &&
              y.id != this.paramId
          )
        );
        this.collectTableValues();
      })
    );
    this.subs.push(
      this.state.nodeItems$.subscribe((x) => {
        this.constructionDayReportNodeItems = x.filter(
          (x) => x.constructionDayReportId
        );
        this.attachedImageNodeItems = x.filter(
          (x) =>
            x.constructionDayReportId == this.paramId &&
            this.state.supportedPhotoFileTypes.includes(x.fileExtension)
        );
        this.attachedDocumentNodeItems = x.filter(
          (x) =>
            x.constructionDayReportId == this.paramId &&
            !this.state.supportedPhotoFileTypes.includes(x.fileExtension)
        );
        this.loadImages();
      })
    );
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
    this.previews.forEach((p) => {
      if (p.imageUrl) {
        URL.revokeObjectURL(p.imageUrl);
      }
    });
  }

  segmentChanged(event: any) {
    this.report = this.sameDayReports.find((x) => x.id == event.detail.value);
    this.attachedImageNodeItems = this.constructionDayReportNodeItems.filter(
      (x) =>
        x.constructionDayReportId == this.report.id &&
        this.state.supportedPhotoFileTypes.includes(x.fileExtension)
    );
    this.attachedDocumentNodeItems = this.constructionDayReportNodeItems.filter(
      (x) =>
        x.constructionDayReportId == this.report.id &&
        !this.state.supportedPhotoFileTypes.includes(x.fileExtension)
    );
    this.loadImages();
  }

  collectTableValues() {
    /* this.tableValues.push({
      value1: 'Arbeitszeit',
      value2: '',
      value3: this.toolService.calculateHours(this.report.workingTimes),
    });*/
    this.report.workingTimes?.forEach((x) =>
      this.tableValues.push({
        value1: this.toolService.formatTime(x.start),
        value2: false,
        value3: this.toolService.formatTime(x.end),
        value4: '-  ',
      })
    );
    this.report.workers.forEach((x) => {
      this.tableValues.push({
        value1: this.state.getUserDisplayName(x),
        value2: true,
        value3: this.toolService.calculateHours(
          this.report.workerTimeEntries[x]
        ),
        value4: '',
      });
      this.report.workerTimeEntries[x].forEach((y) =>
        this.tableValues.push({
          value1: this.toolService.formatTime(y.start),
          value2: false,
          value3: this.toolService.formatTime(y.end),
          value4: '-  ',
        })
      );
    });
  }

  cancel() {
    return this.modalCtrl.dismiss(null, ModalRole.Cancel);
  }

  async viewReport() {
    await this.state.openFile([this.report.id], PdfType.ConstructionDayReport);
  }

  approveReport() {
    this.state.signConstructionDayReport(this.report.id);
    this.cancel();
  }

  editReport() {
    this.modalCtrl.dismiss(this.report.id, ModalRole.Edit);
  }

  async deleteReport() {
    await this.state.deleteReport(this.report);
  }

  async fixErrors() {
    const employeeOption = this.translate.instant('user.type.employee');
    await this.toolService
      .showBasicAlert(
        this.translate.instant('reports.chooseHours'),
        this.translate.instant('reports.adoptHours'),
        employeeOption,
        this.translate.instant('reports.daily')
      )
      .then((x) => {
        if (x != 'backdrop') {
          let takeUserHours = x == employeeOption;
          this.state.resolveHours(takeUserHours, this.report.id);
        }
      });
  }

  private loadImages(): void {
    this.previews = this.attachedImageNodeItems.map(
      (item): PreviewItem => ({
        id: item.id,
        imageUrl: undefined,
        loading: true,
        hasError: false,
      })
    );

    // Jedes Bild einzeln laden
    this.previews.forEach((preview) => {
      this.state.downloadPreviewImage(preview.id).subscribe({
        next: (blob) => {
          preview.imageUrl = URL.createObjectURL(blob);
          preview.loading = false;
        },
        error: () => {
          preview.loading = false;
          preview.hasError = true;
        },
      });
    });
  }

  public async openFile(id: string): Promise<void> {
    const ids: string[] = [
      ...this.attachedImageNodeItems,
      ...this.attachedDocumentNodeItems,
    ].map((item) => item.id);
    const index = ids.indexOf(id);
    await this.state.openFile(ids, PdfType.Document, index);
  }
}
